import {
  CAMERA_PRESENTER,
  RESET_USER_VIRTUAL_BACKGROUND,
  PASSED
} from '@studio/constants';
import { CameraSourceSettings } from '@shoflo/polymer';
import React from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { ModalBody } from 'reactstrap';
import {
  clearModal,
  setAudioInputDevice,
  setAudioOutputDevice,
  setVideoInputDevice,
  setUserBackground,
  setTransformedVideoInputs,
  uploadUserVirtualBackground,
  deleteUserVirtualBackground
} from '@shoflo/studio-redux/actions';
import { AUDIO_VIDEO_SETTINGS_FORM } from '@shoflo/polymer';
import { updatePresenterSettings } from 'actions/presenter';
import { StyledModal } from './SourceSettings.styled';

const SourceSettings = () => {
  const modal = useSelector(state => state.modal);
  const user = useSelector(state => state.user, shallowEqual);
  const meetingSession = useSelector(
    state => state.meeting.meetingSession,
    shallowEqual
  );
  const attendeeResponse = useSelector(
    state => state.meeting.attendeeResponse,
    shallowEqual
  );
  const attendeeCameraStatus = useSelector(
    state => state.attendeeCameraStatus,
    shallowEqual
  );
  const audioInputDevice = useSelector(state => state.audioInputDevice);
  const audioOutputDevice = useSelector(state => state.audioOutputDevice);
  const videoInputDevice = useSelector(state => state.videoInputDevice);
  const userBackground = useSelector(state => state.userBackground);
  const transformedVideoInputs = useSelector(
    state => state.transformedVideoInputs
  );
  const cameraStatus = useSelector(state => state.readinessCheck.camera);

  const dispatch = useDispatch();

  const isCameraAvailable = cameraStatus === PASSED;

  const isPresenterEditForm = modal?.type === AUDIO_VIDEO_SETTINGS_FORM;
  if (!isPresenterEditForm) return null;

  const isOpen =
    isPresenterEditForm && modal.initialValue?.type === CAMERA_PRESENTER;

  const closeModal = () => dispatch(clearModal(modal.type));

  const onCancel = async initialDataPayload => {
    const { transformedVideoInputs, videoInput } = initialDataPayload;

    dispatch(setVideoInputDevice(videoInput));
    dispatch(setTransformedVideoInputs(transformedVideoInputs));

    closeModal();
  };

  const onSubmit = data => {
    const {
      audioInputDeviceId,
      audioOutputDeviceId,
      videoInput,
      title,
      description,
      userBackground,
      transformedVideoInputs
    } = data;

    dispatch(setAudioInputDevice(audioInputDeviceId));
    dispatch(setVideoInputDevice(videoInput));
    dispatch(setAudioOutputDevice(audioOutputDeviceId));
    dispatch(setUserBackground(userBackground));
    dispatch(setTransformedVideoInputs(transformedVideoInputs));

    const attendeeId = attendeeResponse?.Attendee?.AttendeeId ?? '';
    const isCameraOn = attendeeCameraStatus[attendeeId] ?? false;
    if (!isCameraOn) {
      meetingSession.audioVideo.stopVideoInput();
    }

    dispatch(
      updatePresenterSettings(modal.initialValue, {
        source_title: title,
        description
      })
    );
    closeModal();
  };

  const { title, description = '' } = modal.initialValue;

  const handleDeleteUserVirtualBackground = () => {
    dispatch(deleteUserVirtualBackground());
  };
  const handleUploadUserVirtualBackground = file =>
    dispatch(uploadUserVirtualBackground(file));
  const handleResetUserVirtualBackground = () =>
    dispatch({ type: RESET_USER_VIRTUAL_BACKGROUND });

  return (
    <StyledModal isOpen={isOpen} backdrop="static" keyboard="false">
      <ModalBody className="p-1">
        <CameraSourceSettings
          meetingSession={meetingSession}
          title={title}
          description={description}
          onSubmit={onSubmit}
          onCancel={onCancel}
          audioInputDeviceId={audioInputDevice}
          audioOutputDeviceId={audioOutputDevice}
          videoInputDeviceId={videoInputDevice?.device ?? videoInputDevice}
          isCameraAvailable={isCameraAvailable}
          userBackground={userBackground}
          transformedVideoInputs={transformedVideoInputs}
          uploadUserVirtualBackground={handleUploadUserVirtualBackground}
          deleteUserVirtualBackground={handleDeleteUserVirtualBackground}
          resetUserVirtualBackground={handleResetUserVirtualBackground}
          user={user}
        />
      </ModalBody>
    </StyledModal>
  );
};

export { SourceSettings };
